import React, { useEffect, useRef, useState } from 'react';
import { Check, X, ArrowRight, Zap, IndianRupee, AlertTriangle } from 'lucide-react';
import { sapComparisonData } from '../../data/mock';

const SAPComparisonSection = ({ onOpenCalendar }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'white' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: '#FEF3C7', color: '#92400E' }}
          >
            <AlertTriangle size={16} />
            <span className="font-semibold text-sm">Why Pay More?</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {sapComparisonData.title}
          </h2>
          <p 
            className={`display-small mb-4 ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--brand-dark)' }}
          >
            {sapComparisonData.subtitle}
          </p>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-3' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {sapComparisonData.description}
          </p>
          
          {/* Savings Badge */}
          <div 
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full mt-6 ${
              isVisible ? 'animate-fadeInUp stagger-4' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <IndianRupee size={20} />
            <span className="font-bold text-lg">{sapComparisonData.highlight}</span>
          </div>
        </div>

        {/* Comparison Table - 3 Columns */}
        <div 
          className={`max-w-6xl mx-auto mb-12 ${isVisible ? 'animate-scaleIn stagger-5' : 'opacity-0'}`}
        >
          <div className="rounded-3xl overflow-hidden shadow-xl" style={{ background: 'var(--bg-page)' }}>
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-2 md:gap-4 p-4 md:p-6" style={{ background: 'var(--brand-dark)' }}>
              <div className="body-medium font-semibold" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Feature
              </div>
              <div className="text-center">
                <div 
                  className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-full"
                  style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
                >
                  <Check size={14} className="hidden sm:block" />
                  <span className="font-bold text-xs md:text-sm">Zenova</span>
                </div>
              </div>
              <div className="text-center">
                <div 
                  className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-full"
                  style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'rgba(255, 255, 255, 0.6)' }}
                >
                  <X size={14} className="hidden sm:block" />
                  <span className="font-medium text-xs md:text-sm">SAP</span>
                </div>
              </div>
              <div className="text-center">
                <div 
                  className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-full"
                  style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'rgba(255, 255, 255, 0.8)' }}
                >
                  <span className="font-medium text-xs md:text-sm">Others</span>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y" style={{ borderColor: 'rgba(0, 69, 52, 0.1)' }}>
              {sapComparisonData.comparisons.map((row, index) => (
                <div 
                  key={index}
                  className="grid grid-cols-4 gap-2 md:gap-4 p-4 md:p-6 transition-colors hover:bg-white/50"
                >
                  <div>
                    <p className="body-small md:body-medium font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {row.aspect}
                    </p>
                  </div>
                  <div className="text-center">
                    <p 
                      className="font-bold text-xs md:text-sm mb-1"
                      style={{ color: 'var(--brand-dark)' }}
                    >
                      {row.zenova}
                    </p>
                    <p className="text-xs hidden md:block" style={{ color: 'var(--brand-hover)' }}>
                      {row.zenovaNote}
                    </p>
                  </div>
                  <div className="text-center">
                    <p 
                      className="font-medium text-xs md:text-sm mb-1"
                      style={{ color: 'var(--text-light)' }}
                    >
                      {row.sap}
                    </p>
                    <p className="text-xs hidden md:block" style={{ color: '#DC2626' }}>
                      {row.sapNote}
                    </p>
                  </div>
                  <div className="text-center">
                    <p 
                      className="font-medium text-xs md:text-sm mb-1"
                      style={{ color: '#6B7280' }}
                    >
                      {row.others}
                    </p>
                    <p className="text-xs hidden md:block" style={{ color: '#9CA3AF' }}>
                      {row.othersNote}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Line Cards */}
        <div className={`grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '600ms' }}>
          {/* Zenova Card */}
          <div 
            className="p-6 rounded-3xl"
            style={{ background: 'var(--brand-dark)' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--brand-primary)' }}
              >
                <Check size={20} style={{ color: 'var(--brand-dark)' }} />
              </div>
              <h4 className="heading-3" style={{ color: 'white' }}>
                {sapComparisonData.bottomLine.zenova.label}
              </h4>
            </div>
            <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {sapComparisonData.bottomLine.zenova.description}
            </p>
          </div>

          {/* SAP Card */}
          <div 
            className="p-6 rounded-3xl"
            style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: '#FEE2E2' }}
              >
                <X size={20} style={{ color: '#DC2626' }} />
              </div>
              <h4 className="heading-3" style={{ color: '#991B1B' }}>
                {sapComparisonData.bottomLine.sap.label}
              </h4>
            </div>
            <p className="body-medium" style={{ color: '#7F1D1D' }}>
              {sapComparisonData.bottomLine.sap.description}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '700ms' }}>
          <button
            onClick={onOpenCalendar}
            className="btn-primary"
            data-testid="sap-comparison-cta"
          >
            <Zap size={20} />
            {sapComparisonData.cta}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default SAPComparisonSection;
